import { useEffect, useRef, type ReactNode } from "react";

/** Primitivas de interfaz. Poco pero consistente, sin librería de componentes. */

const BASE_CAMPO =
  "w-full rounded-md border px-3 py-2 text-sm outline-none transition " +
  "border-slate-300 bg-white text-slate-900 " +
  "focus:border-marca-500 focus:ring-2 focus:ring-marca-500/20 " +
  "dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100";

const CAMPO_CON_ERROR =
  "border-red-400 focus:border-red-500 focus:ring-red-500/20 dark:border-red-700";

interface CampoProps {
  etiqueta: string;
  error?: string;
  ayuda?: string;
  requerido?: boolean;
  children: ReactNode;
}

export function Campo({ etiqueta, error, ayuda, requerido, children }: CampoProps) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium">
        {etiqueta}
        {requerido && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {children}
      {error ? (
        <span className="block text-xs text-red-600 dark:text-red-400">{error}</span>
      ) : ayuda ? (
        <span className="block text-xs text-slate-500">{ayuda}</span>
      ) : null}
    </label>
  );
}

export function Entrada({
  error,
  className = "",
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { error?: boolean }) {
  return (
    <input
      {...props}
      className={`${BASE_CAMPO} ${error ? CAMPO_CON_ERROR : ""} ${className}`}
    />
  );
}

export function AreaTexto({
  error,
  className = "",
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: boolean }) {
  return (
    <textarea
      {...props}
      className={`${BASE_CAMPO} ${error ? CAMPO_CON_ERROR : ""} ${className}`}
    />
  );
}

export function Seleccion({
  error,
  className = "",
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & { error?: boolean }) {
  return (
    <select
      {...props}
      className={`${BASE_CAMPO} ${error ? CAMPO_CON_ERROR : ""} ${className}`}
    />
  );
}

type Variante = "primario" | "secundario" | "peligro" | "fantasma";

const VARIANTES: Record<Variante, string> = {
  primario: "bg-marca-600 text-white hover:bg-marca-700",
  secundario:
    "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
  peligro:
    "border border-red-300 bg-white text-red-700 hover:bg-red-50 dark:border-red-800 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-950",
  fantasma: "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white",
};

export function Boton({
  variante = "primario",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante }) {
  return (
    <button
      {...props}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${VARIANTES[variante]} ${className}`}
    />
  );
}

/**
 * Botón de solo icono.
 *
 * `etiqueta` es obligatoria a propósito: se usa como `aria-label` para los
 * lectores de pantalla y como `title` para el tooltip. Un icono suelto no
 * dice nada por sí mismo, y con TypeScript exigiendo la prop no hay forma
 * de agregar uno sin explicarlo.
 */
export function BotonIcono({
  etiqueta,
  variante = "neutro",
  className = "",
  children,
  ...props
}: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "aria-label" | "title"> & {
  etiqueta: string;
  variante?: "neutro" | "peligro";
}) {
  const tonos = {
    neutro:
      "text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white",
    peligro:
      "text-slate-500 hover:bg-red-50 hover:text-red-700 dark:text-slate-400 dark:hover:bg-red-950 dark:hover:text-red-400",
  };

  return (
    <button
      {...props}
      aria-label={etiqueta}
      title={etiqueta}
      // 32×32 mínimo: un icono de 16px sin área alrededor es difícil de
      // atinar, sobre todo en pantallas táctiles.
      className={`inline-flex size-8 items-center justify-center rounded-md transition focus-visible:ring-2 focus-visible:ring-marca-500/40 disabled:cursor-not-allowed disabled:opacity-40 ${tonos[variante]} ${className}`}
    >
      {children}
    </button>
  );
}

export function Etiqueta({
  children,
  tono = "neutro",
}: {
  children: ReactNode;
  tono?: "neutro" | "exito" | "alerta" | "peligro";
}) {
  const tonos = {
    neutro: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    exito: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
    alerta: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
    peligro: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tonos[tono]}`}>
      {children}
    </span>
  );
}

export function Aviso({
  tono = "error",
  children,
}: {
  tono?: "error" | "alerta" | "exito";
  children: ReactNode;
}) {
  const tonos = {
    error:
      "border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200",
    alerta:
      "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200",
    exito:
      "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
  };
  return (
    <div className={`rounded-md border px-4 py-3 text-sm ${tonos[tono]}`}>{children}</div>
  );
}

export function Tarjeta({
  titulo,
  acciones,
  children,
}: {
  titulo?: string;
  acciones?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      {(titulo || acciones) && (
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          {titulo && <h2 className="text-sm font-semibold">{titulo}</h2>}
          {acciones}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export interface Pestana<T extends string> {
  clave: T;
  texto: string;
  conteo?: number;
}

/**
 * Pestañas con navegación por teclado.
 *
 * Sigue el patrón de tablist de ARIA: las flechas mueven entre pestañas e
 * Inicio/Fin saltan a los extremos, que es lo que espera quien navega sin
 * ratón. Solo la pestaña activa entra en el orden de tabulación; desde ahí
 * se cambia con flechas.
 */
export function Pestanas<T extends string>({
  pestanas,
  activa,
  alCambiar,
}: {
  pestanas: Pestana<T>[];
  activa: T;
  alCambiar: (clave: T) => void;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  /**
   * Cambiar la pestaña NO basta: hay que llevarse el foco del navegador con
   * ella. Si se queda atrás, el siguiente ArrowRight vuelve a salir desde la
   * pestaña vieja y la navegación por teclado se atora a la segunda tecla.
   */
  const seleccionar = (clave: T) => {
    alCambiar(clave);
    refs.current[clave]?.focus();
  };

  const mover = (desde: number, paso: number) => {
    const total = pestanas.length;
    const destino = pestanas[(desde + paso + total) % total];
    if (destino) seleccionar(destino.clave);
  };

  return (
    <div
      role="tablist"
      aria-label="Categorías"
      // Envuelven en varias filas antes que recortarse: una pestaña que no se
      // ve es una categoría que el usuario da por inexistente.
      className="-mb-px flex flex-wrap gap-x-1 border-b border-slate-200 dark:border-slate-800"
    >
      {pestanas.map((p, i) => {
        const esActiva = p.clave === activa;
        const vacia = p.conteo === 0;

        return (
          <button
            key={p.clave}
            ref={(el) => {
              refs.current[p.clave] = el;
            }}
            role="tab"
            aria-selected={esActiva}
            tabIndex={esActiva ? 0 : -1}
            onClick={() => alCambiar(p.clave)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") mover(i, 1);
              else if (e.key === "ArrowLeft") mover(i, -1);
              else if (e.key === "Home") seleccionar(pestanas[0]!.clave);
              else if (e.key === "End")
                seleccionar(pestanas[pestanas.length - 1]!.clave);
              else return;
              e.preventDefault();
            }}
            className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition ${
              esActiva
                ? "border-marca-600 font-medium text-marca-700 dark:border-marca-500 dark:text-marca-500"
                : `border-transparent hover:border-slate-300 hover:text-slate-900 dark:hover:border-slate-700 dark:hover:text-white ${
                    vacia ? "text-slate-400 dark:text-slate-600" : "text-slate-500"
                  }`
            }`}
          >
            {p.texto}
            {p.conteo !== undefined && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-[11px] tabular-nums ${
                  esActiva
                    ? "bg-marca-100 text-marca-700 dark:bg-marca-500/20 dark:text-marca-500"
                    : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                }`}
              >
                {p.conteo}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function Modal({
  titulo,
  abierto,
  alCerrar,
  children,
  ancho = "max-w-2xl",
}: {
  titulo: string;
  abierto: boolean;
  alCerrar: () => void;
  children: ReactNode;
  ancho?: string;
}) {
  // Escape cierra, y mientras esté abierto el fondo no hace scroll.
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "Escape") alCerrar();
    };
    document.addEventListener("keydown", alTeclear);
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", alTeclear);
      document.body.style.overflow = overflowPrevio;
    };
  }, [abierto, alCerrar]);

  if (!abierto) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-sm"
      onClick={alCerrar}
      role="presentation"
    >
      <div
        className={`my-8 w-full ${ancho} rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3 dark:border-slate-800">
          <h2 className="font-semibold">{titulo}</h2>
          <Boton variante="fantasma" onClick={alCerrar} aria-label="Cerrar">
            ✕
          </Boton>
        </header>
        {children}
      </div>
    </div>
  );
}

export function Confirmacion({
  abierto,
  titulo,
  mensaje,
  textoConfirmar = "Confirmar",
  peligroso,
  alConfirmar,
  alCancelar,
  ocupado,
}: {
  abierto: boolean;
  titulo: string;
  mensaje: ReactNode;
  textoConfirmar?: string;
  peligroso?: boolean;
  alConfirmar: () => void;
  alCancelar: () => void;
  ocupado?: boolean;
}) {
  return (
    <Modal titulo={titulo} abierto={abierto} alCerrar={alCancelar} ancho="max-w-md">
      <div className="space-y-4 p-5">
        <div className="text-sm text-slate-600 dark:text-slate-300">{mensaje}</div>
        <div className="flex justify-end gap-2">
          <Boton variante="secundario" onClick={alCancelar}>
            Cancelar
          </Boton>
          <Boton
            variante={peligroso ? "peligro" : "primario"}
            onClick={alConfirmar}
            disabled={ocupado}
          >
            {ocupado ? "Un momento…" : textoConfirmar}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
